import { useState } from 'react';
import { Profile } from '../lib/types';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { AlertTriangle, Eye, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

// Report shape after normalization (may carry populated reporter/reported objects)
interface NormalizedReport {
  id: string;
  reporterId: string;
  reportedProfileId: string;
  reason: string;
  timestamp: Date;
  status: 'pending' | 'reviewed' | 'resolved';
  // populated objects if API returns them
  reporterData?: { name?: string; email?: string };
  reportedData?: { name?: string; email?: string };
}

interface ReportsManagementProps {
  reports: NormalizedReport[];
  profiles: Profile[];
  onResolveReport: (
    reportId: string,
    action: 'ban' | 'warn' | 'dismiss',
    notes?: string,
  ) => Promise<void>;
}

export function ReportsManagement({
  reports,
  profiles,
  onResolveReport,
}: ReportsManagementProps) {
  const [selectedReport, setSelectedReport] = useState<NormalizedReport | null>(null);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [actionNotes, setActionNotes] = useState('');
  const [resolving, setResolving] = useState<string | null>(null); // action being applied

  // Lookup helper: first check populated API objects, then fall back to profiles array
  const getReporterInfo = (report: NormalizedReport) => {
    if (report.reporterData?.name) return report.reporterData;
    return profiles.find(p => p.id === report.reporterId || (p as any)._id === report.reporterId);
  };

  const getReportedInfo = (report: NormalizedReport) => {
    if (report.reportedData?.name) return report.reportedData;
    return profiles.find(
      p => p.id === report.reportedProfileId || (p as any)._id === report.reportedProfileId,
    );
  };

  const pendingReports = reports.filter(r => r.status === 'pending' || r.status === 'reviewed');
  const resolvedReports = reports.filter(r => r.status === 'resolved');

  const handleViewReport = (report: NormalizedReport) => {
    setSelectedReport(report);
    setShowReportDialog(true);
    setActionNotes('');
  };

  const handleResolve = async (action: 'ban' | 'warn' | 'dismiss') => {
    if (!selectedReport) return;
    setResolving(action);
    try {
      await onResolveReport(selectedReport.id, action, actionNotes || undefined);
      const label =
        action === 'dismiss' ? 'dismissed' : action === 'ban' ? 'user banned' : 'user warned';
      toast.success(`Report ${label} successfully`);
      setShowReportDialog(false);
      setSelectedReport(null);
      setActionNotes('');
    } catch {
      // parent already shows error toast
    } finally {
      setResolving(null);
    }
  };

  const ReportRow = ({ report, showStatus = false }: { report: NormalizedReport; showStatus?: boolean }) => {
    const reported = getReportedInfo(report);
    const reporter = getReporterInfo(report);
    return (
      <TableRow className="hover:bg-orange-50/30 transition-colors">
        <TableCell className="font-mono text-xs text-gray-500">{report.id.slice(-8).toUpperCase()}</TableCell>
        <TableCell>
          <div>
            <p className="font-medium text-gray-900 text-sm">{(reported as any)?.name || '—'}</p>
            <p className="text-xs text-gray-400 truncate max-w-[160px]">{report.reportedProfileId}</p>
          </div>
        </TableCell>
        <TableCell>
          <div>
            <p className="font-medium text-gray-900 text-sm">{(reporter as any)?.name || '—'}</p>
            <p className="text-xs text-gray-400 truncate max-w-[160px]">{report.reporterId}</p>
          </div>
        </TableCell>
        <TableCell>
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
            {report.reason}
          </Badge>
        </TableCell>
        <TableCell className="text-sm text-gray-500">
          {format(report.timestamp, 'MMM dd, yyyy HH:mm')}
        </TableCell>
        {showStatus && (
          <TableCell>
            <Badge className="bg-green-600 border-0 text-white text-xs">Resolved</Badge>
          </TableCell>
        )}
        {!showStatus && (
          <TableCell className="text-right">
            <Button
              size="sm"
              variant="outline"
              className="border-orange-200 hover:bg-orange-50 hover:border-orange-400"
              onClick={() => handleViewReport(report)}
            >
              <Eye className="size-4 mr-1" /> Review
            </Button>
          </TableCell>
        )}
      </TableRow>
    );
  };

  return (
    <div className="space-y-6">
      {/* Pending Reports */}
      <Card className="shadow-lg border-0">
        <CardHeader className="border-b bg-gradient-to-r from-orange-50 to-red-50">
          <CardTitle className="flex items-center gap-2">
            <div className="p-2 bg-orange-500 rounded-lg">
              <AlertTriangle className="size-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                Pending Reports
                <Badge className="bg-orange-500 text-white border-0">{pendingReports.length}</Badge>
              </div>
              <p className="text-sm font-normal text-gray-500 mt-0.5">Require attention</p>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold text-xs">Report ID</TableHead>
                  <TableHead className="font-semibold text-xs">Reported User</TableHead>
                  <TableHead className="font-semibold text-xs">Reported By</TableHead>
                  <TableHead className="font-semibold text-xs">Reason</TableHead>
                  <TableHead className="font-semibold text-xs">Date</TableHead>
                  <TableHead className="font-semibold text-xs text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingReports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-gray-500 py-16">
                      <CheckCircle className="size-12 mx-auto mb-3 text-gray-300" />
                      <p className="font-medium">No pending reports</p>
                      <p className="text-sm text-gray-400 mt-1">All caught up!</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  pendingReports.map(report => (
                    <ReportRow key={report.id} report={report} />
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Resolved Reports */}
      <Card className="shadow-lg border-0">
        <CardHeader className="border-b bg-gradient-to-r from-green-50 to-emerald-50">
          <CardTitle className="flex items-center gap-2">
            <div className="p-2 bg-green-500 rounded-lg">
              <CheckCircle className="size-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                Resolved Reports
                <Badge className="bg-green-500 text-white border-0">{resolvedReports.length}</Badge>
              </div>
              <p className="text-sm font-normal text-gray-500 mt-0.5">Previously handled</p>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold text-xs">Report ID</TableHead>
                  <TableHead className="font-semibold text-xs">Reported User</TableHead>
                  <TableHead className="font-semibold text-xs">Reported By</TableHead>
                  <TableHead className="font-semibold text-xs">Reason</TableHead>
                  <TableHead className="font-semibold text-xs">Date</TableHead>
                  <TableHead className="font-semibold text-xs">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {resolvedReports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                      No resolved reports yet
                    </TableCell>
                  </TableRow>
                ) : (
                  resolvedReports.map(report => (
                    <ReportRow key={report.id} report={report} showStatus />
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Report Review Dialog */}
      <Dialog open={showReportDialog} onOpenChange={open => { if (!resolving) setShowReportDialog(open); }}>
        <DialogContent className="max-w-2xl">
          {selectedReport && (() => {
            const reporter = getReporterInfo(selectedReport);
            const reported = getReportedInfo(selectedReport);
            return (
              <>
                <DialogHeader>
                  <DialogTitle>Review Report</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                  {/* Report Summary */}
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="size-5 text-red-600 mt-0.5 shrink-0" />
                      <div className="flex-1 space-y-1.5 text-sm">
                        <p>
                          <span className="font-medium">Report ID:</span>{' '}
                          <span className="font-mono text-xs">{selectedReport.id}</span>
                        </p>
                        <p>
                          <span className="font-medium">Reason:</span>{' '}
                          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs ml-1">
                            {selectedReport.reason}
                          </Badge>
                        </p>
                        <p>
                          <span className="font-medium">Date:</span>{' '}
                          {format(selectedReport.timestamp, 'MMM dd, yyyy HH:mm')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Parties */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-semibold mb-2 text-sm">Reported User</h4>
                      <div className="space-y-1 text-sm">
                        <p className="font-medium">{(reported as any)?.name || '—'}</p>
                        <p className="text-gray-500 text-xs">{(reported as any)?.email || selectedReport.reportedProfileId}</p>
                        <p className="font-mono text-xs text-gray-400 break-all">{selectedReport.reportedProfileId}</p>
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-semibold mb-2 text-sm">Reported By</h4>
                      <div className="space-y-1 text-sm">
                        <p className="font-medium">{(reporter as any)?.name || '—'}</p>
                        <p className="text-gray-500 text-xs">{(reporter as any)?.email || selectedReport.reporterId}</p>
                        <p className="font-mono text-xs text-gray-400 break-all">{selectedReport.reporterId}</p>
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Action Notes <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <Textarea
                      placeholder="Add notes about your decision…"
                      value={actionNotes}
                      onChange={e => setActionNotes(e.target.value)}
                      rows={3}
                      disabled={!!resolving}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3 justify-end pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => setShowReportDialog(false)}
                      disabled={!!resolving}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleResolve('dismiss')}
                      disabled={!!resolving}
                    >
                      {resolving === 'dismiss' ? (
                        <Loader2 className="size-4 mr-2 animate-spin" />
                      ) : (
                        <XCircle className="size-4 mr-2" />
                      )}
                      Dismiss
                    </Button>
                    <Button
                      variant="outline"
                      className="border-orange-300 text-orange-700 hover:bg-orange-50"
                      onClick={() => handleResolve('warn')}
                      disabled={!!resolving}
                    >
                      {resolving === 'warn' ? (
                        <Loader2 className="size-4 mr-2 animate-spin" />
                      ) : (
                        <AlertTriangle className="size-4 mr-2" />
                      )}
                      Warn User
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleResolve('ban')}
                      disabled={!!resolving}
                    >
                      {resolving === 'ban' ? (
                        <Loader2 className="size-4 mr-2 animate-spin" />
                      ) : (
                        <AlertTriangle className="size-4 mr-2" />
                      )}
                      Ban User
                    </Button>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
